import * as Sentry from "@sentry/react";
import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential
} from "firebase/auth";
import type { ChangeEvent, ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ListCard, ListRow } from "@/components/ui/list-card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";

import { AuthEmailAPI, authEmailErrorCode } from "../../api/authEmailApi";
import { LawyerAPI } from "../../api/lawyerApi";
import { UsersAPI } from "../../api/usersApi";
import type { LawyerDTO } from "../../api/types/lawFirm";
import { CustomPhoneInput } from "../common/PhoneInput";
import { ReauthDialog } from "../ReauthDialog";
import { RepresentativeDetailsSection } from "./RepresentativeDetailsSection";
import {
  EditActions,
  SettingsEditDialog,
  SettingsField,
  SettingsListRow
} from "./settingsParts";
import {
  updateLawyerAvatar,
  updateLawyerData
} from "../../redux/lawyer/lawyerSlice";
import { lawyerSelectors } from "../../redux/lawyer/selectors";

type Props = {
  onRequestClose?: () => void;
};

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

type AccountValues = {
  firstName: string;
  middleName: string;
  lastName: string;
  title: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  email: string;
};

type EditKey = "name" | "email" | "title" | "phone" | "address";

export function AccountTab({ onRequestClose }: Props) {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdateMailError, setIsUpdateMailError] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMailMessage, setErrorMailMessage] = useState("");
  const [needsReauth, setNeedsReauth] = useState(false);

  const [lawyerFromApi, setLawyerFromApi] = useState<LawyerDTO | null>(null);
  const [values, setValues] = useState<AccountValues>({
    firstName: "",
    middleName: "",
    lastName: "",
    title: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipcode: "",
    email: ""
  });
  const [editKey, setEditKey] = useState<EditKey | null>(null);
  const [editError, setEditError] = useState<string>("");

  const auth = getAuth();
  const user = auth.currentUser;

  const {
    firstName,
    middleName,
    lastName,
    title,
    email,
    address,
    state,
    city,
    zipcode,
    phone,
    website,
    avatarSrc
  } = useSelector(lawyerSelectors.selectAll);

  const uid = useSelector(lawyerSelectors.selectUid);

  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!uid) return;
      try {
        const [lawyer, profile] = await Promise.all([
          LawyerAPI.get(uid),
          UsersAPI.getProfile(uid)
        ]);
        setLawyerFromApi(lawyer);
        if (profile.avatar_data_url) {
          dispatch(
            updateLawyerAvatar({
              avatarSrc: profile.avatar_data_url,
              avatarName: ""
            })
          );
        }
      } catch (e) {
        Sentry.captureException(e);
      }
    };
    run();
  }, [uid, dispatch]);

  const handleAvatarFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    // Reset immediately so re-selecting the same file still triggers onChange.
    e.target.value = "";
    if (!uid || !file) return;

    setIsLoading(true);
    try {
      await LawyerAPI.uploadAvatar(uid, file);
      const profile = await UsersAPI.getProfile(uid);
      dispatch(
        updateLawyerAvatar({
          avatarSrc: profile.avatar_data_url ?? "",
          avatarName: ""
        })
      );
      toast.success("Profile photo updated", {
        description: "Your profile photo was uploaded successfully."
      });
    } catch (err) {
      Sentry.captureException(err);
      const message =
        err instanceof Error ? err.message : "Failed to upload profile photo.";
      toast.error("Couldn’t upload profile photo", { description: message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateEmail = async (value: string) => {
    if (value === email || !user) return;
    setIsLoading(true);
    setShowErrorMessage(false);
    try {
      const pending = await AuthEmailAPI.requestEmailChange(value);
      toast.success("Confirm your new email", {
        description: `Check ${pending} to confirm the change.`
      });
      setValues((v) => ({ ...v, email }));
      setEditKey(null);
    } catch (e) {
      if (authEmailErrorCode(e) === "requires_recent_login") {
        setNeedsReauth(true);
        return;
      }
      setErrorMailMessage(
        e instanceof Error ? e.message : "Unable to update email."
      );
      setIsUpdateMailError(true);
      setShowErrorMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReauthenticate = async (password: string) => {
    if (!user?.email) return;
    try {
      await reauthenticateWithCredential(
        user,
        EmailAuthProvider.credential(user.email, password)
      );
      await user.getIdToken(true);
      setNeedsReauth(false);
      await handleUpdateEmail(values.email.trim());
    } catch (e) {
      const code = (e as { code?: string } | null)?.code;
      setErrorMailMessage(
        code === "auth/multi-factor-auth-required"
          ? "Sign out and sign back in, then change your email."
          : "That password didn't work."
      );
      setShowErrorMessage(true);
    }
  };

  const baseline = useMemo(() => {
    const l = lawyerFromApi;
    return {
      firstName: l?.firstName ?? firstName,
      middleName: l?.middleName ?? middleName,
      lastName: l?.lastName ?? lastName,
      title: l?.title ?? title,
      email: l?.email ?? email,
      address: l?.address ?? address,
      state: l?.state ?? state,
      city: l?.city ?? city,
      zipcode: l?.zipcode ?? zipcode,
      phone: l?.phone ?? phone,
      website
    };
  }, [
    lawyerFromApi,
    firstName,
    middleName,
    lastName,
    title,
    email,
    address,
    state,
    city,
    zipcode,
    phone,
    website
  ]);

  useEffect(() => {
    setValues({
      firstName: baseline.firstName || "",
      middleName: baseline.middleName || "",
      lastName: baseline.lastName || "",
      title: baseline.title || "",
      phone: baseline.phone || "",
      address: baseline.address || "",
      city: baseline.city || "",
      state: baseline.state || "",
      zipcode: baseline.zipcode || "",
      email: baseline.email || ""
    });
  }, [baseline]);

  useEffect(() => {
    if (isUpdateMailError) {
      setIsUpdateMailError(false);
    }
  }, [isUpdateMailError]);

  const openEdit = (key: EditKey) => {
    setEditError("");
    setShowErrorMessage(false);
    setErrorMailMessage("");
    setEditKey(key);
  };

  const closeEdit = () => {
    setEditKey(null);
    setEditError("");
    setNeedsReauth(false);
    setShowErrorMessage(false);
  };

  const saveProfilePatch = async (patch: {
    firstName?: string;
    middleName?: string | undefined;
    lastName?: string;
    title?: string | undefined;
    address?: string | undefined;
    city?: string | undefined;
    state?: string | undefined;
    zipcode?: string | undefined;
    phone?: string | undefined;
  }) => {
    if (!uid) return;
    setIsLoading(true);
    try {
      const updatedLawyer = await LawyerAPI.update(uid, patch);
      setLawyerFromApi(updatedLawyer);
      dispatch(
        updateLawyerData({
          firstName: updatedLawyer.firstName,
          middleName: updatedLawyer.middleName ?? "",
          lastName: updatedLawyer.lastName,
          title: updatedLawyer.title ?? "",
          address: updatedLawyer.address ?? "",
          state: updatedLawyer.state ?? "",
          city: updatedLawyer.city ?? "",
          phone: updatedLawyer.phone ?? "",
          zipcode: updatedLawyer.zipcode ?? ""
        })
      );
      setValues((v) => ({
        ...v,
        firstName: updatedLawyer.firstName ?? v.firstName,
        middleName: updatedLawyer.middleName ?? "",
        lastName: updatedLawyer.lastName ?? v.lastName,
        title: updatedLawyer.title ?? "",
        address: updatedLawyer.address ?? "",
        state: updatedLawyer.state ?? "",
        city: updatedLawyer.city ?? "",
        phone: updatedLawyer.phone ?? "",
        zipcode: updatedLawyer.zipcode ?? ""
      }));
      closeEdit();
    } catch (e) {
      Sentry.captureException(e);
      setEditError("Failed to save changes. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const fullName = [values.firstName, values.lastName]
    .filter(Boolean)
    .join(" ");
  const editTitle: Record<EditKey, string> = {
    name: "Edit Name",
    email: "Edit Email",
    phone: "Edit Phone Number",
    title: "Edit Title",
    address: "Edit Address"
  };
  let editBody: ReactNode = null;
  if (editKey === "name") {
    editBody = (
      <>
        <SettingsField
          label="First Name"
          value={values.firstName}
          onChange={(e) =>
            setValues((v) => ({ ...v, firstName: e.target.value }))
          }
          placeholder="First name"
        />
        <SettingsField
          label="Middle Name"
          value={values.middleName}
          onChange={(e) =>
            setValues((v) => ({ ...v, middleName: e.target.value }))
          }
          placeholder="Middle name"
        />
        <SettingsField
          label="Last Name"
          value={values.lastName}
          onChange={(e) =>
            setValues((v) => ({ ...v, lastName: e.target.value }))
          }
          placeholder="Last name"
        />
        <EditActions
          saving={isLoading}
          onCancel={closeEdit}
          onSave={() => {
            if (!values.firstName.trim() || !values.lastName.trim()) {
              setEditError("First name and last name are required.");
              return;
            }
            saveProfilePatch({
              firstName: values.firstName.trim(),
              middleName: values.middleName.trim() || undefined,
              lastName: values.lastName.trim()
            });
          }}
        />
      </>
    );
  } else if (editKey === "email") {
    editBody = (
      <>
        <SettingsField
          label="Email"
          value={values.email}
          aria-invalid={showErrorMessage || undefined}
          onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
          placeholder="Email"
        />
        {showErrorMessage ? (
          <p className="text-sm text-danger">
            {errorMailMessage || "Unable to update email."}
          </p>
        ) : null}
        {needsReauth ? (
          <ReauthDialog
            actionText="change your email"
            onReauthenticate={handleReauthenticate}
          />
        ) : (
          <EditActions
            saving={isLoading}
            onCancel={closeEdit}
            onSave={() => {
              const nextEmail = values.email.trim();
              if (!nextEmail) {
                setEditError("Email is required.");
                return;
              }
              handleUpdateEmail(nextEmail);
            }}
          />
        )}
      </>
    );
  } else if (editKey === "phone") {
    editBody = (
      <>
        <CustomPhoneInput
          value={values.phone}
          onChange={(nextPhone) =>
            setValues((v) => ({ ...v, phone: nextPhone }))
          }
          label="Phone Number"
          placeholder="Phone Number"
          inputWidth="100%"
        />
        <EditActions
          saving={isLoading}
          onCancel={closeEdit}
          onSave={() =>
            saveProfilePatch({ phone: values.phone.trim() || undefined })
          }
        />
      </>
    );
  } else if (editKey === "title") {
    editBody = (
      <>
        <div className="flex flex-col gap-1.5">
          <Label>Title</Label>
          <Select
            value={values.title || undefined}
            onValueChange={(v) => setValues((s) => ({ ...s, title: v }))}
          >
            <SelectTrigger className="w-full" aria-label="Title">
              <SelectValue placeholder="Select title" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Mr.">Mr.</SelectItem>
              <SelectItem value="Ms.">Ms.</SelectItem>
              <SelectItem value="Dr.">Dr.</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <EditActions
          saving={isLoading}
          onCancel={closeEdit}
          onSave={() =>
            saveProfilePatch({ title: values.title.trim() || undefined })
          }
        />
      </>
    );
  } else if (editKey === "address") {
    editBody = (
      <>
        <SettingsField
          label="Address"
          value={values.address}
          onChange={(e) =>
            setValues((v) => ({ ...v, address: e.target.value }))
          }
          placeholder="Address"
        />
        <div className="flex gap-3">
          <SettingsField
            label="City"
            className="flex-1"
            value={values.city}
            onChange={(e) => setValues((v) => ({ ...v, city: e.target.value }))}
            placeholder="City"
          />
          <SettingsField
            label="State"
            className="flex-1"
            value={values.state}
            onChange={(e) =>
              setValues((v) => ({ ...v, state: e.target.value }))
            }
            placeholder="State"
          />
        </div>
        <SettingsField
          label="ZIP Code"
          value={values.zipcode}
          onChange={(e) =>
            setValues((v) => ({ ...v, zipcode: e.target.value }))
          }
          placeholder="ZIP code"
        />
        <EditActions
          saving={isLoading}
          onCancel={closeEdit}
          onSave={() =>
            saveProfilePatch({
              address: values.address.trim() || undefined,
              city: values.city.trim() || undefined,
              state: values.state.trim() || undefined,
              zipcode: values.zipcode.trim() || undefined
            })
          }
        />
      </>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-162 flex-col gap-7 px-6 pt-4 pb-8">
      <input
        type="file"
        ref={fileRef}
        onChange={handleAvatarFileChange}
        accept="image/*"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
      />

      <ListCard>
        <ListRow
          media={
            <Avatar size="lg">
              {avatarSrc ? <AvatarImage src={avatarSrc} alt="" /> : null}
              <AvatarFallback>{initialsOf(fullName)}</AvatarFallback>
            </Avatar>
          }
          title={fullName || "—"}
          description={
            <span className="text-sm text-fg-3">
              Role:{" "}
              <span className="capitalize">
                {lawyerFromApi?.role?.replace(/[_-]+/g, " ")}
              </span>
            </span>
          }
          control={
            <Button
              variant="secondary"
              size="sm"
              disabled={isLoading}
              onClick={() => fileRef.current?.click()}
            >
              {isLoading ? <Spinner /> : null}
              Change Photo
            </Button>
          }
        />
        <SettingsListRow
          label="Name"
          value={fullName}
          onClick={() => openEdit("name")}
        />
        <SettingsListRow
          label="Email"
          value={values.email || "—"}
          onClick={() => openEdit("email")}
        />
        <SettingsListRow
          label="Phone Number"
          value={values.phone || "—"}
          onClick={() => openEdit("phone")}
        />
        <SettingsListRow
          label="Title"
          value={values.title || "—"}
          onClick={() => openEdit("title")}
        />
        <SettingsListRow
          label="Address"
          value={
            [values.address, values.city, values.state, values.zipcode]
              .filter(Boolean)
              .join(", ") || "—"
          }
          onClick={() => openEdit("address")}
        />
      </ListCard>

      <RepresentativeDetailsSection
        lawyerId={uid}
        profile={lawyerFromApi?.representative_profile}
        onSaved={setLawyerFromApi}
      />

      <SettingsEditDialog
        open={editKey !== null}
        title={editKey ? editTitle[editKey] : ""}
        error={editError}
        onClose={closeEdit}
      >
        {editBody}
      </SettingsEditDialog>
    </div>
  );
}
